// En-tête SNK (sections/snk-header.liquid).
// <snk-header> : le burger ouvre le menu <dialog> (showModal : focus piégé, Échap, retour du focus) ; tiroir depuis la
// gauche, plein écran sur mobile, 420 px sur ordi. Ferme au clic hors du panneau.
// <snk-lang> : menu déroulant des langues sous le bouton (flèches haut/bas, Début/Fin), fermé au clic dehors, avec
// Échap et Tab ; chaque langue est un lien vers la même page traduite (/en/…, /it/…).
// Ordi (option A) : boutons Marques / Accessoires de la barre → panneaux sous l'en-tête (data-snk-panel) ; clic, ou
// survol avec un court délai ; un seul ouvert ; Échap (focus rendu au bouton), clic dehors ou sortie de l'en-tête ferment.
if (!customElements.get('snk-header')) {
  customElements.define(
    'snk-header',
    class SnkHeader extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-snk-menu-open]');
        this.dialog = this.querySelector('dialog');
        this.header = this.querySelector('.snk-hdr');

        this.button.addEventListener('click', () => (this.dialog.open ? this.close() : this.open()));
        this.querySelectorAll('[data-snk-menu-close]').forEach((button) =>
          button.addEventListener('click', () => this.close())
        );
        // Échap : même fermeture animée que le bouton
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
        // Clic sur le fond, hors du panneau
        this.dialog.addEventListener('click', (event) => {
          if (event.target !== this.dialog) return;
          const box = this.dialog.getBoundingClientRect();
          const inside =
            event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
          if (!inside) this.close();
        });
        this.dialog.addEventListener('close', () => {
          this.button.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('snk-menu-open');
        });

        // Éditeur de thème : ouvre le menu quand on sélectionne un de ses blocs
        this.onBlockSelect = (event) => {
          if (this.dialog.contains(event.target) && !this.dialog.open) this.open();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.setupPanels();
      }

      setupPanels() {
        this.toggles = [...this.querySelectorAll('[data-snk-panel]')];
        if (!this.toggles.length) return;
        const box = (toggle) => this.querySelector(`[data-snk-panel-box="${toggle.dataset.snkPanel}"]`);
        const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.closePanels = (except = null) => {
          this.toggles.forEach((toggle) => {
            if (toggle === except) return;
            const panel = box(toggle);
            toggle.setAttribute('aria-expanded', 'false');
            if (!panel || panel.hidden) return;
            panel.classList.remove('is-open');
            clearTimeout(panel.hideTimer);
            panel.hideTimer = setTimeout(() => (panel.hidden = true), 200);
          });
        };
        this.openPanel = (toggle) => {
          const panel = box(toggle);
          if (!panel) return;
          this.closePanels(toggle);
          clearTimeout(panel.hideTimer);
          panel.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
          requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
        };
        this.toggles.forEach((toggle) => {
          toggle.addEventListener('click', () => (toggle.getAttribute('aria-expanded') === 'true' ? this.closePanels() : this.openPanel(toggle)));
          toggle.addEventListener('mouseenter', () => {
            if (!hover.matches) return;
            clearTimeout(this.leaveTimer);
            clearTimeout(this.enterTimer);
            this.enterTimer = setTimeout(() => this.openPanel(toggle), 120);
          });
          toggle.addEventListener('mouseleave', () => clearTimeout(this.enterTimer));
        });
        // Sortie de l'en-tête et de ses panneaux (souris) : fermeture après un court délai
        this.addEventListener('mouseleave', () => {
          if (!hover.matches) return;
          clearTimeout(this.enterTimer);
          this.leaveTimer = setTimeout(() => this.closePanels(), 250);
        });
        this.addEventListener('mouseenter', () => clearTimeout(this.leaveTimer));
        // Échap, où que soit le focus, tant qu'un panneau est ouvert
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          const open = this.toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true');
          if (!open) return;
          this.closePanels();
          open.focus();
        });
        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.closePanels();
        });
        // Focus qui sort de l'en-tête (Tab) : on ferme
        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closePanels();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      // Fermeture aussi soignée que l'ouverture (LG, 4 nov. 2026) : le tiroir repart vers la gauche et le voile s'efface
      // (snk-header.css › .is-closing), puis le dialog se ferme ; sans animation si prefers-reduced-motion
      close() {
        if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.dialog.close();
          return;
        }
        this.dialog.classList.add('is-closing');
        const done = () => {
          clearTimeout(this.closeTimer);
          this.dialog.removeEventListener('animationend', onEnd);
          this.dialog.classList.remove('is-closing');
          this.dialog.close();
        };
        const onEnd = (event) => {
          if (event.target === this.dialog) done();
        };
        this.dialog.addEventListener('animationend', onEnd);
        this.closeTimer = setTimeout(done, 400);
      }

      open() {
        // Sur ordi, le méga-menu commence sous l'en-tête
        const top = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
        this.dialog.style.setProperty('--snk-menu-top', `${top}px`);
        this.dialog.showModal();
        this.button.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('snk-menu-open');
      }
    }
  );
}

if (!customElements.get('snk-lang')) {
  customElements.define(
    'snk-lang',
    class SnkLang extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.snk-lang__toggle');
        this.options = [...this.querySelectorAll('.snk-lang__option')];
        this.toggle.addEventListener('click', () => (this.classList.contains('is-open') ? this.close() : this.open()));
        this.addEventListener('keydown', (event) => this.onKey(event));
        this.onOutside = (event) => {
          if (!this.contains(event.target)) this.close();
        };
      }

      onKey(event) {
        if (!this.classList.contains('is-open')) {
          if (event.target === this.toggle && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            this.open();
          }
          return;
        }
        const index = this.options.indexOf(document.activeElement);
        const last = this.options.length - 1;
        const moves = {
          ArrowDown: index < last ? index + 1 : 0,
          ArrowUp: index > 0 ? index - 1 : last,
          Home: 0,
          End: last,
        };
        if (event.key in moves) {
          event.preventDefault();
          this.options[moves[event.key]].focus();
        } else if (event.key === 'Escape') {
          this.close();
          this.toggle.focus();
        } else if (event.key === 'Tab') {
          this.close();
        }
      }

      open() {
        this.classList.add('is-open');
        this.toggle.setAttribute('aria-expanded', 'true');
        (this.options.find((option) => option.hasAttribute('aria-current')) || this.options[0])?.focus({ preventScroll: true });
        document.addEventListener('click', this.onOutside);
      }

      close() {
        this.classList.remove('is-open');
        this.toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onOutside);
      }
    }
  );
}
